import { NextPage } from 'next';
import Head from 'next/head';
import NextImageLegacy from 'next/legacy/image';
import NextImage from 'next/image';
import { ParsedUrlQuery } from 'querystring';

import { CacheLandingPage } from '@sy/ssr-types';
import LandingPageTemplate from '@sy/app-client';
import FriendLandingPageTemplate from '@sy/app-client/src/ssrAdapter/templates/FriendLandingPageTemplate';
import PartnerLandingPageTemplate from '@sy/app-client/src/ssrAdapter/templates/PartnerLandingPageTemplate';
import JsonLandingPageTemplate from '@sy/app-client/src/ssrAdapter/templates/JsonLandingPageTemplate';

import MarketingTemplate from '~/components/templates/MarketingTemplate';
import { buildGetServerSideProps } from '~/getServerSideProps';
import {
	buildPageMetadata,
	getPageData,
	getPageType,
} from '~/utils/dynamicSlugPages';
import type { PageProps } from '~/utils/dynamicSlugPages';
import { getMarketingNavigation } from '~/utils/getNavigation';
import {
	isNoIndexedDuplicatePath,
	getOriginalSlug,
} from '~/utils/noIndexedUtils';

import type { BlogCategoryUrl } from '~/services/blog/categories';
import type { CommonContext, InferProps } from '~/getServerSideProps';

const NoIndexHead = () => (
	<Head>
		<meta name="robots" content="noindex" />
	</Head>
);

const LandingPage: NextPage<InferProps<typeof getServerSideProps>> = ({
	data,
	featureFlags,
	isNoIndexed,
	navigation,
	pageType,
	user,
}) => {
	const shouldNoIndex =
		isNoIndexed || pageType === 'affiliatePage' || pageType === 'partnerPage';

	if (pageType === 'affiliatePage') {
		const { image, name, referrerId } = data;
		return (
			<>
				{shouldNoIndex && <NoIndexHead />}
				<MarketingTemplate
					featureFlags={featureFlags}
					navigation={navigation}
					user={user}
				>
					<FriendLandingPageTemplate
						image={
							<NextImageLegacy alt={name} height={50} src={image} width={50} />
						}
						name={name}
						referrerId={referrerId}
					/>
				</MarketingTemplate>
			</>
		);
	}

	if (pageType === 'partnerPage') {
		const { image, name } = data;

		const isIHeart = name === 'IHeart';

		return (
			<>
				{shouldNoIndex && <NoIndexHead />}
				<MarketingTemplate
					featureFlags={featureFlags}
					navigation={navigation}
					user={user}
				>
					{isIHeart && (
						// eslint-disable-next-line @next/next/no-img-element, jsx-a11y/alt-text
						<img
							src="https://arttrk.com/pixel/?ad_log=referer&action=content&pixid=dfd0fbd9-e7bd-4f9b-83ca-25f7bee979d8"
							width="1"
							height="1"
						/>
					)}
					<PartnerLandingPageTemplate
						{...data}
						image={
							image ? (
								<NextImageLegacy
									alt={name}
									height={50}
									src={image}
									width={50}
								/>
							) : undefined
						}
					/>
				</MarketingTemplate>
			</>
		);
	}

	if (pageType === 'jsonLandingPage') {
		return (
			<>
				{shouldNoIndex && <NoIndexHead />}
				<MarketingTemplate
					featureFlags={featureFlags}
					navigation={navigation}
					user={user}
				>
					<JsonLandingPageTemplate data={data} />
				</MarketingTemplate>
			</>
		);
	}

	return (
		<>
			{shouldNoIndex && <NoIndexHead />}
			<MarketingTemplate
				featureFlags={featureFlags}
				activePage={`/${data.urlPath}`}
				navigation={navigation}
				user={user}
			>
				<LandingPageTemplate NextImage={NextImage} data={data} />
			</MarketingTemplate>
		</>
	);
};

interface UrlParams extends ParsedUrlQuery {
	slug?: string;
	categoryUrl?: BlogCategoryUrl;
}

const customServerSideProps = async (context: CommonContext<UrlParams>) => {
	const { auth, featureFlags, locale, query, req } = context;

	/*
		1. If the `dynamic-slug` is set in packages/ssr-routing/src/index.ts
		and there is not any page template created, we will end up here with /[dynamic-slug]

		2. If somehow we end up here and dynamicSlug is undefined or we can not fetch the
		data from Contentful, we just return a 404
	*/

	const [dynamicSlug] = query['dynamic-slug'] as [string | undefined];
	const isPreview = !!query?.preview;
	const enforceLocale = !!query?.enforceLocale;

	if (!dynamicSlug) {
		return {
			notFound: true,
		};
	}

	const isNoIndexed = isNoIndexedDuplicatePath(dynamicSlug);
	// Get the original slug in case it's a duplicate path (e.g., for noindex duplicates)
	const originalSlug = getOriginalSlug(dynamicSlug);

	const pageType = getPageType(dynamicSlug);

	const [pageData, navigation] = await Promise.all([
		getPageData({
			dynamicSlug: originalSlug,
			enforceLocale,
			isPreview,
			locale,
			pageType,
			isAuthenticated: auth.isAuthenticated,
		}),
		getMarketingNavigation({ featureFlags, locale, query }),
	]);

	if (!pageData) {
		return {
			notFound: true,
		};
	}

	if (
		pageType === 'landingPage' &&
		featureFlags.orion.landingPageRedesignV2 &&
		[
			'recordings',
			'podcasting',
			'streamyard-on-air',
			'video-repurposing',
		].includes(originalSlug)
	) {
		const content = (pageData as CacheLandingPage).content || [];
		const responsiveImageIndex = content.findIndex(
			item => item.type === 'responsiveImage',
		);

		// insert a mobileSignupForm item right after the responsiveImage
		if (responsiveImageIndex !== -1) {
			(pageData as CacheLandingPage).content = [
				...content.slice(0, responsiveImageIndex + 1),
				{ type: 'mobileSignupForm' },
				...content.slice(responsiveImageIndex + 1),
			];
		}
	}

	const pageProps = {
		data: pageData,
		pageType,
	} as PageProps;
	const host = req.headers.host as string;

	return {
		props: {
			...pageProps,
			isNoIndexed,
			metadata: buildPageMetadata({ host, locale, pageProps }),
			navigation,
		},
	};
};

export default LandingPage;

// TODO: different list of translationNamespaces for each pageType
export const getServerSideProps = buildGetServerSideProps({
	customServerSideProps,
	translationNamespaces: [
		'common',
		'auth',
		'landing',
		'landingRecordingsSimplified',
		'landingMainPage',
		'landingStreamyardObsComparison',
	],
});
